import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  PrinterIcon,
  DocumentTextIcon,
  ReceiptPercentIcon,
  BanknotesIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import PrintableInvoiceTemplate from '../components/insurance/PrintableInvoiceTemplate';
import DetailedInvoiceTemplate from '../components/insurance/DetailedInvoiceTemplate';
import { exportToPDF, printElement } from '../utils/pdfExport';
import MonthlyInvoiceManagement from './MonthlyInvoiceManagement';

interface InsuranceInvoice {
  id: number;
  numero: string;
  date_emission: string;
  patient: {
    id: number;
    nom: string;
    prenoms: string;
    numero_assure: string;
  };
  assureur: {
    id: number;
    nom: string;
  };
  montant_assurance: number;
  insurance_paid: number;
  insurance_pending: number;
  submission_status: 'pending' | 'submitted' | 'partially_paid' | 'paid';
  insurance_submitted_at: string | null;
  notes: string;
}

interface PaymentFormData {
  montant: string;
  date_paiement: string;
  reference: string;
  notes: string;
}

interface BulkPaymentItem {
  invoice: InsuranceInvoice;
  montant: string;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; icon: React.ReactNode }> = {
  pending: {
    label: 'En attente',
    bg: 'son-chip-amber', text: '',
    icon: <XCircleIcon className="w-3.5 h-3.5" aria-hidden />,
  },
  submitted: {
    label: 'Soumis',
    bg: 'son-chip-blue', text: '',
    icon: <ClockIcon className="w-3.5 h-3.5" aria-hidden />,
  },
  partially_paid: {
    label: 'Part. payé',
    bg: 'son-chip-purple', text: '',
    icon: <BanknotesIcon className="w-3.5 h-3.5" aria-hidden />,
  },
  paid: {
    label: 'Payé',
    bg: 'son-chip-green', text: '',
    icon: <CheckCircleIcon className="w-3.5 h-3.5" aria-hidden />,
  },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, bg: 'son-chip-gray', text: '', icon: null };
  return (
    <span className={`son-chip ${cfg.bg} ${cfg.text}`}>
      {cfg.icon}{cfg.label}
    </span>
  );
}

// Classes explicites : un `text-${color}-600` construit dynamiquement n'est pas généré par Tailwind
const KPI_TONE: Record<string, string> = {
  blue: 'text-[#1565B8]',
  indigo: 'text-[color:var(--text)]',
  green: 'text-[#0E8466]',
  yellow: 'text-[#B5781A]',
};

export default function InsuranceManagement() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<'invoices' | 'monthly'>('invoices');
  const [invoices, setInvoices] = useState<InsuranceInvoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<InsuranceInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  const [filters, setFilters] = useState({
    assureur: '',
    status: '',
    search: '',
    start_date: '',
    end_date: '',
  });
  const [insuranceCompanies, setInsuranceCompanies] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);

  // Detail modal
  const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState<any>(null);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const detailPrintRef = useRef<HTMLDivElement>(null);

  // Payment modal
  const [paymentTarget, setPaymentTarget] = useState<InsuranceInvoice | null>(null);
  const [paymentForm, setPaymentForm] = useState<PaymentFormData>({
    montant: '',
    date_paiement: new Date().toISOString().split('T')[0],
    reference: '',
    notes: '',
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Bulk payment modal
  const [bulkPaymentOpen, setBulkPaymentOpen] = useState(false);
  const [bulkPaymentItems, setBulkPaymentItems] = useState<BulkPaymentItem[]>([]);
  const [bulkPaymentDate, setBulkPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [bulkPaymentReference, setBulkPaymentReference] = useState('');
  const [bulkPaymentLoading, setBulkPaymentLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadInsuranceCompanies();
  }, []);

  useEffect(() => {
    if (location.pathname === '/monthly-invoices') setActiveTab('monthly');
    else if (location.pathname === '/insurance-management') setActiveTab('invoices');
  }, [location]);

  useEffect(() => {
    applyFilters();
  }, [filters, invoices]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/insurance/invoices/', {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      setInvoices(response.data.invoices || []);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error loading insurance invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInsuranceCompanies = async () => {
    try {
      const response = await api.get('/assureurs/');
      setInsuranceCompanies(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error loading insurance companies:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...invoices];

    if (filters.assureur) {
      filtered = filtered.filter(inv => inv.assureur.id === parseInt(filters.assureur));
    }

    if (filters.status) {
      filtered = filtered.filter(inv => inv.submission_status === filters.status);
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.numero.toLowerCase().includes(search) ||
        inv.patient.nom.toLowerCase().includes(search) ||
        inv.patient.prenoms.toLowerCase().includes(search) ||
        (inv.patient.numero_assure ?? '').toLowerCase().includes(search)
      );
    }

    // Date range — compare ISO date strings (YYYY-MM-DD), lexicographic sort works
    if (filters.start_date) {
      filtered = filtered.filter(inv => inv.date_emission >= filters.start_date);
    }
    if (filters.end_date) {
      filtered = filtered.filter(inv => inv.date_emission <= filters.end_date);
    }

    setFilteredInvoices(filtered);
  };

  // ── selection ────────────────────────────────────────────────────────────
  const handleSelectAll = () => {
    setSelectedInvoices(
      selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0
        ? []
        : filteredInvoices.map(inv => inv.id)
    );
  };
  const handleSelectInvoice = (id: number) => {
    setSelectedInvoices(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // ── bulk submit ──────────────────────────────────────────────────────────
  const handleMarkAsSubmitted = async () => {
    if (selectedInvoices.length === 0) {
      alert('Veuillez sélectionner au moins une facture');
      return;
    }
    if (!confirm(`Marquer ${selectedInvoices.length} facture(s) comme soumise(s) à l'assurance ?`)) return;

    try {
      await api.post('/dashboard/insurance/payment-tracking/', {
        invoice_ids: selectedInvoices,
        notes: `Soumis le ${new Date().toLocaleDateString('fr-FR')}`,
      });
      setSelectedInvoices([]);
      await loadData();
      alert('Factures soumises avec succès');
    } catch (error) {
      console.error(error);
      alert('Erreur lors de la soumission');
    }
  };

  // ── single-row submit ────────────────────────────────────────────────────
  const handleSubmitSingle = async (invoice: InsuranceInvoice) => {
    if (!confirm(`Soumettre la facture ${invoice.numero} à l'assurance ?`)) return;
    try {
      await api.patch(`/dashboard/insurance/invoices/${invoice.id}/`, { mark_submitted: true });
      await loadData();
    } catch (error) {
      console.error(error);
      alert('Erreur lors de la soumission');
    }
  };

  // ── payment modal ────────────────────────────────────────────────────────
  const openPaymentModal = (invoice: InsuranceInvoice) => {
    setPaymentTarget(invoice);
    setPaymentForm({
      montant: invoice.insurance_pending.toFixed(0),
      date_paiement: new Date().toISOString().split('T')[0],
      reference: '',
      notes: '',
    });
  };

  const handlePaymentSubmit = async () => {
    if (!paymentTarget) return;
    const montant = parseFloat(paymentForm.montant);
    if (!montant || montant <= 0) {
      alert('Veuillez saisir un montant valide');
      return;
    }
    setPaymentLoading(true);
    try {
      await api.patch(`/dashboard/insurance/invoices/${paymentTarget.id}/`, {
        record_payment: {
          montant,
          date_paiement: paymentForm.date_paiement,
          reference: paymentForm.reference,
          notes: paymentForm.notes,
        },
      });
      setPaymentTarget(null);
      await loadData();
    } catch (error: any) {
      console.error(error);
      alert(error.response?.data?.date_paiement?.[0] || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setPaymentLoading(false);
    }
  };

  // ── bulk payment ─────────────────────────────────────────────────────────
  const openBulkPaymentModal = () => {
    const eligible = filteredInvoices.filter(
      inv => selectedInvoices.includes(inv.id) &&
        (inv.submission_status === 'submitted' || inv.submission_status === 'partially_paid')
    );
    if (eligible.length === 0) {
      alert('Aucune facture soumise parmi la sélection. Sélectionnez des factures avec le statut "Soumis" ou "Part. payé".');
      return;
    }
    setBulkPaymentItems(eligible.map(inv => ({ invoice: inv, montant: inv.insurance_pending.toFixed(0) })));
    setBulkPaymentDate(new Date().toISOString().split('T')[0]);
    setBulkPaymentReference('');
    setBulkPaymentOpen(true);
  };

  const handleBulkPaymentSubmit = async () => {
    const invalid = bulkPaymentItems.find(item => !parseFloat(item.montant) || parseFloat(item.montant) <= 0);
    if (invalid) {
      alert(`Montant invalide pour la facture ${invalid.invoice.numero}`);
      return;
    }
    setBulkPaymentLoading(true);
    try {
      await Promise.all(
        bulkPaymentItems.map(item =>
          api.patch(`/dashboard/insurance/invoices/${item.invoice.id}/`, {
            record_payment: {
              montant: parseFloat(item.montant),
              date_paiement: bulkPaymentDate,
              reference: bulkPaymentReference,
              notes: 'Paiement groupé',
            },
          })
        )
      );
      setBulkPaymentOpen(false);
      setSelectedInvoices([]);
      await loadData();
    } catch (error: any) {
      console.error(error);
      alert(error.response?.data?.date_paiement?.[0] || 'Erreur lors de l\'enregistrement des paiements');
      await loadData(); // les autres factures du lot ont pu être réglées
    } finally {
      setBulkPaymentLoading(false);
    }
  };

  // ── export ───────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (filteredInvoices.length === 0) return;
    const csvData = filteredInvoices.map(inv => ({
      'N° Facture': inv.numero,
      'Date': inv.date_emission,
      'Patient': `${inv.patient.nom} ${inv.patient.prenoms}`,
      'N° Assuré': inv.patient.numero_assure,
      'Assurance': inv.assureur.nom,
      'Montant Assurance': inv.montant_assurance,
      'Payé': inv.insurance_paid,
      'En Attente': inv.insurance_pending,
      'Statut': inv.submission_status,
    }));
    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factures_assurance_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrintInvoices = async () => {
    if (selectedInvoices.length === 0 || !filters.assureur) {
      alert('Veuillez sélectionner des factures et filtrer par assurance');
      return;
    }
    setIsPrinting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      if (printRef.current) printElement(printRef.current);
    } catch (error) {
      alert('Erreur lors de l\'impression');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleExportPDF = async () => {
    if (selectedInvoices.length === 0 || !filters.assureur) {
      alert('Veuillez sélectionner des factures et filtrer par assurance');
      return;
    }
    setIsExportingPDF(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      if (printRef.current) {
        const insuranceName = insuranceCompanies.find(ins => ins.id === parseInt(filters.assureur))?.nom || 'Assurance';
        await exportToPDF(printRef.current, `bordereau_${insuranceName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`, 'portrait');
      }
    } catch (error) {
      alert('Erreur lors de l\'export PDF');
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleViewInvoiceDetail = async (invoiceId: number) => {
    try {
      const response = await api.get(`/dashboard/insurance/invoices/${invoiceId}/`, {
        params: { _t: Date.now() },
      });
      setSelectedInvoiceDetail(response.data);
    } catch (error) {
      alert('Erreur lors du chargement des détails');
    }
  };

  const handlePrintDetailedInvoice = async () => {
    if (!selectedInvoiceDetail) return;
    await new Promise(resolve => setTimeout(resolve, 100));
    if (detailPrintRef.current) printElement(detailPrintRef.current);
  };

  const handleExportDetailedPDF = async () => {
    if (!selectedInvoiceDetail) return;
    await new Promise(resolve => setTimeout(resolve, 100));
    if (detailPrintRef.current) {
      await exportToPDF(detailPrintRef.current, `facture_${selectedInvoiceDetail.numero}_${new Date().toISOString().split('T')[0]}.pdf`, 'portrait');
    }
  };

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header */}
        <div className="mb-5 space-y-4">
          <div className="son-page-head">
            <div className="flex items-center gap-3">
              <div className="stat-icon-teal !h-11 !w-11">
                <ShieldCheckIcon className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <h1 className="son-h1">Gestion des Assurances</h1>
                <p className="son-sub !mt-0.5">Export et suivi des factures d'assurance</p>
              </div>
            </div>
            <button onClick={() => navigate('/dashboard')} className="btn-secondary">
              Retour au Dashboard
            </button>
          </div>

          {/* Tabs */}
          <div>
            <nav className="son-tabs" role="tablist">
              {[
                { id: 'invoices', label: 'Factures Individuelles', icon: DocumentTextIcon },
                { id: 'monthly', label: 'Factures Mensuelles', icon: ReceiptPercentIcon },
              ].map(tab => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    role="tab"
                    aria-selected={active}
                    className={`son-tab ${active ? 'active' : ''}`}
                  >
                    <Icon className="w-4 h-4" aria-hidden />{tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Summary cards */}
          {activeTab === 'invoices' && summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {[
                { label: 'Total', value: summary.total_count, unit: '', color: 'blue' },
                { label: 'Montant total', value: summary.total_insurance_amount?.toLocaleString('fr-FR'), unit: ' GNF', color: 'indigo' },
                { label: 'Payé', value: summary.total_paid?.toLocaleString('fr-FR'), unit: ' GNF', color: 'green' },
                { label: 'En attente', value: summary.total_pending?.toLocaleString('fr-FR'), unit: ' GNF', color: 'yellow' },
                { label: '# En attente', value: summary.count_pending, unit: ' fac.', color: 'yellow' },
                { label: '# Soumis', value: summary.count_submitted, unit: ' fac.', color: 'blue' },
                { label: '# Payé', value: summary.count_paid, unit: ' fac.', color: 'green' },
              ].map((card, i) => (
                <div key={i} className="son-kpi !block !p-3">
                  <div className="son-kpi-label truncate">{card.label}</div>
                  <div className={`text-[16px] font-extrabold tabular-nums truncate ${KPI_TONE[card.color]}`}>{card.value}{card.unit}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {activeTab === 'invoices' && (
          <>
            {/* Filters */}
            <div className="son-toolbar mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                <div className="son-search !min-w-0">
                  <MagnifyingGlassIcon aria-hidden />
                  <input
                    type="search"
                    aria-label="Rechercher une facture"
                    placeholder="Rechercher..."
                    className="form-input text-gray-700"
                    value={filters.search}
                    onChange={e => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>

                <select
                  aria-label="Filtrer par assurance"
                  className="form-select text-gray-700"
                  value={filters.assureur}
                  onChange={e => setFilters({ ...filters, assureur: e.target.value })}
                >
                  <option value="">Toutes les assurances</option>
                  {insuranceCompanies.map(ins => (
                    <option key={ins.id} value={ins.id}>{ins.nom}</option>
                  ))}
                </select>

                <select
                  aria-label="Filtrer par statut"
                  className="form-select text-gray-700"
                  value={filters.status}
                  onChange={e => setFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="submitted">Soumis</option>
                  <option value="partially_paid">Partiellement payé</option>
                  <option value="paid">Payé</option>
                </select>

                <div className="flex flex-col">
                  <label htmlFor="ins-start" className="son-field-label">Date début</label>
                  <input
                    id="ins-start"
                    type="date"
                    className="form-input text-gray-700"
                    value={filters.start_date}
                    onChange={e => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </div>

                <div className="flex flex-col">
                  <label htmlFor="ins-end" className="son-field-label">Date fin</label>
                  <input
                    id="ins-end"
                    type="date"
                    className="form-input text-gray-700"
                    value={filters.end_date}
                    onChange={e => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </div>

                <button
                  onClick={() => setFilters({ assureur: '', status: '', search: '', start_date: '', end_date: '' })}
                  className="btn-secondary"
                >
                  Réinitialiser
                </button>
              </div>

              <div className="flex flex-wrap justify-between items-center gap-3">
                <span className="text-[13px] text-[color:var(--muted)]">
                  {selectedInvoices.length > 0 ? `${selectedInvoices.length} sélectionnée(s) · ` : ''}
                  {filteredInvoices.length} facture(s)
                </span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleMarkAsSubmitted}
                    disabled={selectedInvoices.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[13px] font-semibold transition-colors disabled:opacity-40 bg-[color:var(--blue-soft)] text-[#1565B8] hover:bg-[#D3E6F8]"
                  >
                    <PaperAirplaneIcon className="w-4 h-4" />
                    Marquer soumis ({selectedInvoices.length})
                  </button>
                  {(() => {
                    const eligible = filteredInvoices.filter(
                      inv => selectedInvoices.includes(inv.id) &&
                        (inv.submission_status === 'submitted' || inv.submission_status === 'partially_paid')
                    );
                    return (
                      <button
                        onClick={openBulkPaymentModal}
                        disabled={eligible.length === 0}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[13px] font-semibold transition-colors disabled:opacity-40 bg-[color:var(--teal-soft)] text-[#0E8466] hover:bg-[#CDEDE2]"
                      >
                        <BanknotesIcon className="w-4 h-4" />
                        Paiement reçu ({eligible.length})
                      </button>
                    );
                  })()}
                  <button
                    onClick={handlePrintInvoices}
                    disabled={selectedInvoices.length === 0 || !filters.assureur || isPrinting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[13px] font-semibold transition-colors disabled:opacity-40 bg-white border border-[color:var(--border)] text-[color:var(--text)] hover:bg-[color:var(--panel-2)]"
                  >
                    {isPrinting
                      ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />Impression...</>
                      : <><PrinterIcon className="w-4 h-4" />Imprimer</>}
                  </button>
                  <button
                    onClick={handleExportPDF}
                    disabled={selectedInvoices.length === 0 || !filters.assureur || isExportingPDF}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[13px] font-semibold transition-colors disabled:opacity-40 bg-white border border-[color:var(--border)] text-[color:var(--text)] hover:bg-[color:var(--panel-2)]"
                  >
                    {isExportingPDF
                      ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />Export...</>
                      : <><DocumentTextIcon className="w-4 h-4" />PDF</>}
                  </button>
                  <button
                    onClick={handleExport}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[13px] font-semibold transition-colors disabled:opacity-40 bg-white border border-[color:var(--border)] text-[color:var(--text)] hover:bg-[color:var(--panel-2)]"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4" />CSV
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="son-table-wrap">
              <div className="overflow-x-auto">
                <table className="son-table">
                  <thead>
                    <tr>
                      <th className="w-10">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          title="Sélectionner tout"
                          aria-label="Sélectionner toutes les factures"
                        />
                      </th>
                      <th>N° Facture</th>
                      <th>Date</th>
                      <th>Patient</th>
                      <th>Assurance</th>
                      <th className="!text-right">Montant</th>
                      <th className="!text-right">Payé</th>
                      <th className="!text-right">Restant</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10} aria-busy="true">
                          <span className="sr-only">Chargement…</span>
                          <div className="space-y-3 py-2">
                            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="son-skeleton h-8" />)}
                          </div>
                        </td>
                      </tr>
                    ) : filteredInvoices.length === 0 ? (
                      <tr>
                        <td colSpan={10}>
                          <div className="son-empty">
                            <div className="son-empty-icon"><DocumentTextIcon className="h-6 w-6" aria-hidden /></div>
                            <p className="font-semibold text-[color:var(--text)]">Aucune facture trouvée</p>
                            <p>Ajustez les filtres pour élargir la recherche.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredInvoices.map(invoice => (
                        <tr key={invoice.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedInvoices.includes(invoice.id)}
                              onChange={() => handleSelectInvoice(invoice.id)}
                              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              aria-label={`Sélectionner la facture ${invoice.numero}`}
                            />
                          </td>

                          <td className="whitespace-nowrap">
                            <button
                              type="button"
                              className="font-bold tabular-nums text-[color:var(--blue)] hover:underline"
                              onClick={() => handleViewInvoiceDetail(invoice.id)}
                              title="Voir le détail"
                            >
                              {invoice.numero}
                            </button>
                          </td>

                          <td className="whitespace-nowrap tabular-nums text-[color:var(--muted)]">
                            {new Date(invoice.date_emission).toLocaleDateString('fr-FR')}
                          </td>

                          <td>
                            <button
                              type="button"
                              className="font-semibold text-left hover:text-[color:var(--blue)] hover:underline"
                              onClick={() => navigate(`/patients/${invoice.patient.id}`)}
                              title="Voir le dossier patient"
                            >
                              {invoice.patient.nom} {invoice.patient.prenoms}
                            </button>
                            {invoice.patient.numero_assure && (
                              <div className="text-[11.5px] text-[color:var(--muted)]">{invoice.patient.numero_assure}</div>
                            )}
                          </td>

                          <td className="whitespace-nowrap text-[color:var(--muted)]">
                            {invoice.assureur.nom}
                          </td>

                          <td className="whitespace-nowrap text-right font-bold tabular-nums">
                            {invoice.montant_assurance.toLocaleString('fr-FR')} GNF
                          </td>

                          <td className="whitespace-nowrap text-right font-semibold tabular-nums text-[#0E8466]">
                            {invoice.insurance_paid.toLocaleString('fr-FR')} GNF
                          </td>

                          <td className={`whitespace-nowrap text-right font-semibold tabular-nums ${invoice.insurance_pending > 0 ? 'text-[#B5781A]' : 'text-[color:var(--muted)]'}`}>
                            {invoice.insurance_pending.toLocaleString('fr-FR')} GNF
                          </td>

                          <td className="whitespace-nowrap">
                            <StatusBadge status={invoice.submission_status} />
                            {invoice.insurance_submitted_at && (
                              <div className="text-[11.5px] text-[color:var(--muted)] mt-0.5 tabular-nums">
                                {new Date(invoice.insurance_submitted_at).toLocaleDateString('fr-FR')}
                              </div>
                            )}
                          </td>

                          <td className="whitespace-nowrap">
                            {invoice.submission_status === 'pending' && (
                              <button
                                onClick={() => handleSubmitSingle(invoice)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[color:var(--blue-soft)] text-[#1565B8] hover:bg-[#D3E6F8] transition-colors"
                              >
                                <PaperAirplaneIcon className="w-3 h-3" />Soumettre
                              </button>
                            )}
                            {(invoice.submission_status === 'submitted' || invoice.submission_status === 'partially_paid') && (
                              <button
                                onClick={() => openPaymentModal(invoice)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[color:var(--teal-soft)] text-[#0E8466] hover:bg-[#CDEDE2] transition-colors"
                              >
                                <BanknotesIcon className="w-3 h-3" />Paiement reçu
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Hidden printable components */}
            <div className="hidden">
              {selectedInvoices.length > 0 && filters.assureur && (
                <PrintableInvoiceTemplate
                  ref={printRef}
                  invoices={filteredInvoices.filter(inv => selectedInvoices.includes(inv.id)) as any}
                  insuranceName={insuranceCompanies.find(ins => ins.id === parseInt(filters.assureur))?.nom || ''}
                  period={{
                    start: filters.start_date || new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    end: filters.end_date || new Date().toISOString().split('T')[0],
                  }}
                />
              )}
              {selectedInvoiceDetail && (
                <DetailedInvoiceTemplate ref={detailPrintRef} invoice={selectedInvoiceDetail} />
              )}
            </div>

            {/* Bulk payment modal */}
            {bulkPaymentOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Enregistrer paiements en masse</h2>
                      <p className="text-sm text-gray-500 mt-0.5">{bulkPaymentItems.length} facture(s) soumise(s)</p>
                    </div>
                    <button onClick={() => setBulkPaymentOpen(false)} className="text-gray-400 hover:text-gray-600">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="px-6 py-4 space-y-4 flex-shrink-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date de paiement *</label>
                        <input
                          type="date"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 text-sm focus:border-emerald-500 focus:ring-emerald-500"
                          value={bulkPaymentDate}
                          onChange={e => setBulkPaymentDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Référence commune (optionnel)</label>
                        <input
                          type="text"
                          placeholder="N° virement, chèque..."
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 text-sm focus:border-emerald-500 focus:ring-emerald-500"
                          value={bulkPaymentReference}
                          onChange={e => setBulkPaymentReference(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="overflow-y-auto flex-1 px-6">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr>
                          <th className="py-2 text-left font-medium text-gray-600">Facture</th>
                          <th className="py-2 text-left font-medium text-gray-600">Patient</th>
                          <th className="py-2 text-right font-medium text-gray-600">Dû</th>
                          <th className="py-2 text-right font-medium text-gray-600 w-36">Montant reçu (GNF)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {bulkPaymentItems.map((item, idx) => (
                          <tr key={item.invoice.id} className="hover:bg-gray-50">
                            <td className="py-2 font-medium text-blue-600">{item.invoice.numero}</td>
                            <td className="py-2 text-gray-700">{item.invoice.patient.nom} {item.invoice.patient.prenoms}</td>
                            <td className="py-2 text-right text-amber-600 font-medium whitespace-nowrap">
                              {item.invoice.insurance_pending.toLocaleString('fr-FR')} GNF
                            </td>
                            <td className="py-2 pl-3">
                              <input
                                type="number"
                                min={0}
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-gray-700 text-sm text-right focus:border-emerald-500 focus:ring-emerald-500"
                                value={item.montant}
                                onChange={e => {
                                  const updated = [...bulkPaymentItems];
                                  updated[idx] = { ...item, montant: e.target.value };
                                  setBulkPaymentItems(updated);
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-300 bg-emerald-50">
                          <td colSpan={2} className="py-2.5 font-semibold text-gray-700">Total</td>
                          <td className="py-2.5 text-right font-semibold text-amber-700 whitespace-nowrap">
                            {bulkPaymentItems.reduce((s, i) => s + i.invoice.insurance_pending, 0).toLocaleString('fr-FR')} GNF
                          </td>
                          <td className="py-2.5 pl-3 text-right font-bold text-emerald-700 whitespace-nowrap">
                            {bulkPaymentItems.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0).toLocaleString('fr-FR')} GNF
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
                    <button
                      onClick={() => setBulkPaymentOpen(false)}
                      className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleBulkPaymentSubmit}
                      disabled={bulkPaymentLoading}
                      className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {bulkPaymentLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />}
                      <BanknotesIcon className="w-4 h-4" />
                      Enregistrer {bulkPaymentItems.length} paiement(s)
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Payment modal */}
            {paymentTarget && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-bold text-gray-900">Enregistrer paiement assurance</h2>
                    <button onClick={() => setPaymentTarget(null)} className="text-gray-400 hover:text-gray-600">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="px-6 py-4 space-y-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="font-medium text-gray-900">{paymentTarget.numero} — {paymentTarget.patient.nom} {paymentTarget.patient.prenoms}</div>
                      <div className="text-gray-500 mt-1">
                        Assurance : {paymentTarget.assureur.nom}<br />
                        Montant dû : <span className="font-semibold text-gray-800">{paymentTarget.montant_assurance.toLocaleString('fr-FR')} GNF</span><br />
                        Déjà reçu : <span className="text-green-600 font-semibold">{paymentTarget.insurance_paid.toLocaleString('fr-FR')} GNF</span><br />
                        Restant : <span className="text-amber-600 font-semibold">{paymentTarget.insurance_pending.toLocaleString('fr-FR')} GNF</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Montant reçu (GNF) *</label>
                      <input
                        type="number"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        value={paymentForm.montant}
                        onChange={e => setPaymentForm({ ...paymentForm, montant: e.target.value })}
                        min={0}
                        max={paymentTarget.montant_assurance}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de paiement *</label>
                      <input
                        type="date"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        value={paymentForm.date_paiement}
                        onChange={e => setPaymentForm({ ...paymentForm, date_paiement: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Référence (optionnel)</label>
                      <input
                        type="text"
                        placeholder="N° de virement, chèque..."
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        value={paymentForm.reference}
                        onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
                      <textarea
                        rows={2}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-700 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        value={paymentForm.notes}
                        onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                      onClick={() => setPaymentTarget(null)}
                      className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handlePaymentSubmit}
                      disabled={paymentLoading}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                      {paymentLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />}
                      <BanknotesIcon className="w-4 h-4" />
                      Enregistrer le paiement
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Invoice detail modal */}
            {selectedInvoiceDetail && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900">Facture {selectedInvoiceDetail.numero}</h2>
                    <div className="flex gap-2">
                      {selectedInvoiceDetail.submission_status !== 'paid' && (
                        <button
                          onClick={() => {
                            const inv = invoices.find(i => i.id === selectedInvoiceDetail.id);
                            if (inv) { setSelectedInvoiceDetail(null); openPaymentModal(inv); }
                          }}
                          className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 inline-flex items-center text-sm gap-1"
                        >
                          <BanknotesIcon className="w-4 h-4" />Paiement reçu
                        </button>
                      )}
                      <button onClick={handlePrintDetailedInvoice} className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 inline-flex items-center text-sm gap-1">
                        <PrinterIcon className="w-4 h-4" />Imprimer
                      </button>
                      <button onClick={handleExportDetailedPDF} className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 inline-flex items-center text-sm gap-1">
                        <DocumentTextIcon className="w-4 h-4" />PDF
                      </button>
                      <button onClick={() => setSelectedInvoiceDetail(null)} className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                        Fermer
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-3">Patient</h3>
                        <div className="space-y-1.5 text-sm">
                          <div><span className="text-gray-500">Nom :</span> <span className="text-gray-900 ml-1 font-medium">{selectedInvoiceDetail.patient.nom} {selectedInvoiceDetail.patient.prenoms}</span></div>
                          <div><span className="text-gray-500">N° assuré :</span> <span className="text-gray-900 ml-1">{selectedInvoiceDetail.patient.numero_assure}</span></div>
                          {selectedInvoiceDetail.patient.nom_souscripteur && (
                            <div><span className="text-gray-500">Souscripteur :</span> <span className="text-gray-900 ml-1">{selectedInvoiceDetail.patient.nom_souscripteur}</span></div>
                          )}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-3">Assurance</h3>
                        <div className="space-y-1.5 text-sm">
                          <div><span className="text-gray-500">Compagnie :</span> <span className="text-gray-900 ml-1 font-medium">{selectedInvoiceDetail.assureur.nom}</span></div>
                          <div><span className="text-gray-500">Tél :</span> <span className="text-gray-900 ml-1">{selectedInvoiceDetail.assureur.telephone}</span></div>
                          <div><span className="text-gray-500">Taux :</span> <span className="text-gray-900 ml-1">{selectedInvoiceDetail.assureur.taux_couverture}%</span></div>
                        </div>
                      </div>
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-3">Actes Médicaux</h3>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
                      {selectedInvoiceDetail.lignes && selectedInvoiceDetail.lignes.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Acte</th>
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-600 uppercase">Qté</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">P.U.</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Montant</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {selectedInvoiceDetail.lignes.map((ligne: any, i: number) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-gray-900">{ligne.treatment}</td>
                                <td className="px-4 py-2 text-center text-gray-600">{ligne.quantite}</td>
                                <td className="px-4 py-2 text-right text-gray-600">{ligne.prix_unitaire.toLocaleString('fr-FR')} GNF</td>
                                <td className="px-4 py-2 text-right font-medium text-gray-900">{ligne.montant.toLocaleString('fr-FR')} GNF</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="p-6 text-center text-gray-400 text-sm">Aucun acte médical enregistré</p>
                      )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2 text-sm">
                      <div className="flex justify-between text-gray-700">
                        <span>Montant total :</span>
                        <span className="font-semibold">{selectedInvoiceDetail.montant_total?.toLocaleString('fr-FR')} GNF</span>
                      </div>
                      <div className="flex justify-between text-gray-700">
                        <span>Part assurance :</span>
                        <span className="font-semibold text-blue-700">{selectedInvoiceDetail.montant_assurance?.toLocaleString('fr-FR')} GNF</span>
                      </div>
                      <div className="flex justify-between text-gray-700">
                        <span>Payé par assurance :</span>
                        <span className="font-semibold text-green-700">{selectedInvoiceDetail.insurance_paid?.toLocaleString('fr-FR')} GNF</span>
                      </div>
                      <div className="flex justify-between text-gray-700">
                        <span>Restant dû :</span>
                        <span className="font-semibold text-amber-700">{selectedInvoiceDetail.insurance_pending?.toLocaleString('fr-FR')} GNF</span>
                      </div>
                      <div className="flex justify-between text-gray-700">
                        <span>Part patient :</span>
                        <span className="font-semibold text-gray-900">{selectedInvoiceDetail.montant_patient?.toLocaleString('fr-FR')} GNF</span>
                      </div>
                    </div>

                    {/* Paiements reçus */}
                    {selectedInvoiceDetail.payments && selectedInvoiceDetail.payments.length > 0 && (
                      <div className="mt-4">
                        <h3 className="font-semibold text-gray-900 mb-2 text-sm">Paiements reçus</h3>
                        <div className="space-y-1">
                          {selectedInvoiceDetail.payments.map((p: any) => (
                            <div key={p.id} className="flex justify-between text-sm bg-green-50 rounded px-3 py-1.5">
                              <span className="text-gray-600">{new Date(p.date_paiement).toLocaleDateString('fr-FR')}{p.reference ? ` — Réf: ${p.reference}` : ''}</span>
                              <span className="font-semibold text-green-700">{p.montant.toLocaleString('fr-FR')} GNF</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'monthly' && (
          <div>
            <MonthlyInvoiceManagement />
          </div>
        )}
      </div>
    </div>
  );
}
